import { compact, usd } from "@/lib/format";
import type { Totals } from "@/lib/usage";

/**
 * The dashboard's lead: what a month of usage would have cost at API rates,
 * against the flat subscription spend it actually cost. This is the one hero
 * figure on the page — every other number is a stat tile or a chart.
 */
export default function SummaryBar({ totals }: { totals: Totals }) {
  const { mtdCostUsd, monthlySpendUsd, last7dTokens, activeTools } = totals;
  // A multiple only means something once there's real spend on both sides.
  const multiple =
    monthlySpendUsd > 0 && mtdCostUsd > 0 ? mtdCostUsd / monthlySpendUsd : null;

  return (
    <section className="mb-6 overflow-hidden rounded-2xl border border-border bg-surface">
      <div className="grid gap-px bg-border sm:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <div className="bg-surface px-5 py-5 sm:px-6 sm:py-6">
          <p className="text-xs text-muted">API-equivalent value this month</p>
          <p className="mt-1.5 text-5xl font-semibold leading-none tracking-tight">
            {usd(mtdCostUsd)}
          </p>
          <p className="mt-2.5 text-xs text-muted">
            against{" "}
            <span className="tnum text-foreground">
              {usd(monthlySpendUsd)}/mo
            </span>{" "}
            in subscriptions
            {multiple && (
              <>
                {" — "}
                <span className="tnum font-medium text-good">
                  {multiple.toFixed(1)}× back
                </span>
              </>
            )}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-px bg-border">
          <Tile label="Tokens, last 7 days" value={compact(last7dTokens)} />
          <Tile label="Tools with activity" value={String(activeTools)} />
        </div>
      </div>
    </section>
  );
}

function Tile({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col justify-center bg-surface px-5 py-5">
      <p className="text-xs text-muted">{label}</p>
      <p className="mt-1 text-2xl font-semibold leading-none tracking-tight">
        {value}
      </p>
    </div>
  );
}
